import React, { useCallback, useState, useEffect } from 'react';
import { FormGroup, Intent } from '@blueprintjs/core';

import { MoneyInputGroup } from '@/components/Forms/MoneyInputGroup';
import { CLASSES } from '@/constants/classes';
import { CellType } from '@/constants';
import { parseNumber } from '@/utils/format';
import { CellRendererProps } from './types';

/**
 * Normalizes the incoming money value for display. Amounts coming from the
 * server are decimals with a fixed scale (e.g. "1060.00000"), which the money
 * input renders verbatim as "1 060,00000". Trailing zeros beyond the 2 usual
 * decimals are trimmed, while genuinely more precise values (e.g. "10.4484")
 * are kept untouched.
 */
export const normalizeMoneyValue = (value: any) => {
  if (value === '' || value === null || value === undefined) return value;

  const stringified = String(value);

  if (!/^-?\d*\.\d+$/.test(stringified)) return value;

  const [integer, decimals] = stringified.split('.');
  const trimmed = decimals.replace(/0+$/, '');
  const kept = trimmed.length > 2 ? trimmed : decimals.slice(0, 2);

  return `${integer}.${kept}`;
};

// Input form cell renderer.
const MoneyFieldCellRenderer = ({
  row: { index, moneyInputGroupProps = {} },
  column: { id },
  cell: { value: initialValue },
  payload: { errors, updateData },
}: CellRendererProps) => {
  const [value, setValue] = useState(() => normalizeMoneyValue(initialValue));

  const handleFieldChange = useCallback(
    (value: any) => {
      setValue(value);
    },
    [setValue],
  );

  const handleFieldBlur = () => {
    const parsed = parseNumber(value);
    updateData(index, id, parsed === null ? value : parsed);
  };

  // Select the whole amount when the cell gains focus. Money values are masked
  // and rendered with their decimals already filled (e.g. "21,530"), so a plain
  // click lands the caret at the end — inside a decimal part that's already at
  // the limit — and any digit the user types is silently dropped by the mask.
  // Selecting on focus makes a click-and-type replace the value, which is what
  // users expect when editing an amount in the grid.
  const handleFieldFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  useEffect(() => {
    setValue(normalizeMoneyValue(initialValue));
  }, [initialValue]);

  const error = errors?.[index]?.[id];

  return (
    <FormGroup
      intent={error ? Intent.DANGER : undefined}
      className={CLASSES.FILL}
    >
      <MoneyInputGroup
        value={value}
        // prefix={'$'}
        onChange={handleFieldChange}
        onBlur={handleFieldBlur}
        onFocus={handleFieldFocus}
        {...moneyInputGroupProps}
      />
    </FormGroup>
  );
};

MoneyFieldCellRenderer.cellType = CellType.Field;

export default MoneyFieldCellRenderer;
